import React, { useState, useEffect } from 'react';

const GRADIENT_DIRECTIONS = [
  { value: 'to right', label: '→', title: 'Left to Right' },
  { value: 'to left', label: '←', title: 'Right to Left' },
  { value: 'to bottom', label: '↓', title: 'Top to Bottom' },
  { value: 'to top', label: '↑', title: 'Bottom to Top' },
  { value: 'to bottom right', label: '↘', title: 'Diagonal Down' },
  { value: 'to top right', label: '↗', title: 'Diagonal Up' },
];

const PRESET_GRADIENTS = [
  { start: '#6366f1', end: '#8b5cf6', name: 'Indigo Purple' },
  { start: '#0f172a', end: '#1e293b', name: 'Dark Slate' },
  { start: '#1e3a5f', end: '#0f172a', name: 'Deep Blue' },
  { start: '#7c3aed', end: '#ec4899', name: 'Purple Pink' },
  { start: '#059669', end: '#0d9488', name: 'Emerald Teal' },
  { start: '#f59e0b', end: '#ef4444', name: 'Amber Red' },
];

const parseBackground = (value) => {
  if (!value) return { type: 'solid', color: '#0f172a', start: '#6366f1', end: '#8b5cf6', direction: 'to right' };

  // Check if it's a gradient
  const gradientMatch = value.match(/linear-gradient\(([^,]+),\s*([^,]+),\s*([^)]+)\)/);
  if (gradientMatch) {
    return {
      type: 'gradient',
      direction: gradientMatch[1].trim(),
      start: gradientMatch[2].trim(),
      end: gradientMatch[3].trim(),
      color: gradientMatch[2].trim(),
    };
  }

  // Solid color
  return { type: 'solid', color: value, start: '#6366f1', end: '#8b5cf6', direction: 'to right' };
};

const BackgroundPicker = ({ value, onChange, label = 'Background', textColor, onTextColorChange, showTextColor = false }) => {
  const parsed = parseBackground(value);
  const [type, setType] = useState(parsed.type);
  const [color, setColor] = useState(parsed.color);
  const [gradientStart, setGradientStart] = useState(parsed.start);
  const [gradientEnd, setGradientEnd] = useState(parsed.end);
  const [direction, setDirection] = useState(parsed.direction);

  useEffect(() => {
    const parsed = parseBackground(value);
    setType(parsed.type);
    setColor(parsed.color);
    setGradientStart(parsed.start);
    setGradientEnd(parsed.end);
    setDirection(parsed.direction);
  }, [value]);

  const emitChange = (newType, newColor, newStart, newEnd, newDirection) => {
    if (newType === 'solid') {
      onChange(newColor);
    } else {
      onChange(`linear-gradient(${newDirection}, ${newStart}, ${newEnd})`);
    }
  };

  const handleTypeChange = (newType) => {
    setType(newType);
    emitChange(newType, color, gradientStart, gradientEnd, direction);
  };

  const handleColorChange = (newColor) => {
    setColor(newColor);
    emitChange(type, newColor, gradientStart, gradientEnd, direction);
  };

  const handleGradientStartChange = (newStart) => {
    setGradientStart(newStart);
    emitChange(type, color, newStart, gradientEnd, direction);
  };

  const handleGradientEndChange = (newEnd) => {
    setGradientEnd(newEnd);
    emitChange(type, color, gradientStart, newEnd, direction);
  };

  const handleDirectionChange = (newDirection) => {
    setDirection(newDirection);
    emitChange(type, color, gradientStart, gradientEnd, newDirection);
  };

  const applyPreset = (preset) => {
    setGradientStart(preset.start);
    setGradientEnd(preset.end);
    emitChange('gradient', color, preset.start, preset.end, direction);
  };

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-300">{label}</label>

      {/* Type Toggle */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => handleTypeChange('solid')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            type === 'solid'
              ? 'bg-purple-600 text-white'
              : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
          }`}
        >
          Solid
        </button>
        <button
          type="button"
          onClick={() => handleTypeChange('gradient')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            type === 'gradient'
              ? 'bg-purple-600 text-white'
              : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
          }`}
        >
          Gradient
        </button>
      </div>

      {type === 'solid' ? (
        <div className="flex gap-2 items-center">
          <input
            type="color"
            value={color || '#0f172a'}
            onChange={(e) => handleColorChange(e.target.value)}
            className="w-12 h-10 rounded cursor-pointer border-0"
          />
          <input
            type="text"
            value={color || ''}
            onChange={(e) => handleColorChange(e.target.value)}
            placeholder="#0f172a"
            className="w-32 px-3 py-2 rounded-lg bg-slate-700/50 border border-slate-600 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono text-sm"
          />
        </div>
      ) : (
        <div className="space-y-3">
          {/* Preview */}
          <div
            className="h-12 rounded-lg border border-slate-600"
            style={{ background: `linear-gradient(${direction}, ${gradientStart}, ${gradientEnd})` }}
          />

          {/* Colors */}
          <div className="flex gap-4">
            <div className="flex-1">
              <label className="block text-xs text-gray-400 mb-1">Start Color</label>
              <div className="flex gap-2 items-center">
                <input
                  type="color"
                  value={gradientStart}
                  onChange={(e) => handleGradientStartChange(e.target.value)}
                  className="w-10 h-8 rounded cursor-pointer border-0"
                />
                <input
                  type="text"
                  value={gradientStart}
                  onChange={(e) => handleGradientStartChange(e.target.value)}
                  className="flex-1 px-2 py-1.5 rounded-lg bg-slate-700/50 border border-slate-600 text-white font-mono text-xs"
                />
              </div>
            </div>
            <div className="flex-1">
              <label className="block text-xs text-gray-400 mb-1">End Color</label>
              <div className="flex gap-2 items-center">
                <input
                  type="color"
                  value={gradientEnd}
                  onChange={(e) => handleGradientEndChange(e.target.value)}
                  className="w-10 h-8 rounded cursor-pointer border-0"
                />
                <input
                  type="text"
                  value={gradientEnd}
                  onChange={(e) => handleGradientEndChange(e.target.value)}
                  className="flex-1 px-2 py-1.5 rounded-lg bg-slate-700/50 border border-slate-600 text-white font-mono text-xs"
                />
              </div>
            </div>
          </div>

          {/* Direction */}
          <div>
            <label className="block text-xs text-gray-400 mb-1">Direction</label>
            <div className="flex gap-1">
              {GRADIENT_DIRECTIONS.map((dir) => (
                <button
                  key={dir.value}
                  type="button"
                  title={dir.title}
                  onClick={() => handleDirectionChange(dir.value)}
                  className={`w-8 h-8 rounded-lg text-lg flex items-center justify-center transition-all ${
                    direction === dir.value
                      ? 'bg-purple-600 text-white'
                      : 'bg-slate-700 text-gray-400 hover:bg-slate-600'
                  }`}
                >
                  {dir.label}
                </button>
              ))}
            </div>
          </div>

          {/* Presets */}
          <div>
            <label className="block text-xs text-gray-400 mb-1">Presets</label>
            <div className="flex gap-2 flex-wrap">
              {PRESET_GRADIENTS.map((preset, idx) => (
                <button
                  key={idx}
                  type="button"
                  title={preset.name}
                  onClick={() => applyPreset(preset)}
                  className="w-10 h-6 rounded border border-slate-500 hover:border-purple-500 transition-colors"
                  style={{ background: `linear-gradient(to right, ${preset.start}, ${preset.end})` }}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Text Color */}
      {showTextColor && (
        <div className="pt-3 border-t border-slate-700">
          <label className="block text-xs text-gray-400 mb-2">Text Color</label>
          <div className="flex gap-2 items-center">
            <input
              type="color"
              value={textColor || '#ffffff'}
              onChange={(e) => onTextColorChange(e.target.value)}
              className="w-10 h-8 rounded cursor-pointer border-0"
            />
            <input
              type="text"
              value={textColor || ''}
              onChange={(e) => onTextColorChange(e.target.value)}
              placeholder="#ffffff"
              className="w-28 px-2 py-1.5 rounded-lg bg-slate-700/50 border border-slate-600 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono text-xs"
            />
            {/* Quick presets */}
            <div className="flex gap-1 ml-2">
              {['#ffffff', '#f1f5f9', '#94a3b8', '#1e293b', '#0f172a'].map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => onTextColorChange(c)}
                  className={`w-6 h-6 rounded border ${textColor === c ? 'border-purple-500 ring-1 ring-purple-500' : 'border-slate-500'}`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BackgroundPicker;
